// Э-3. Недельный план: «дни × заводы» с объёмом и загрузкой; клик по ячейке — график дня.
// Правка к ТЗ (встреча 30.09): по умолчанию окно «скользит» — 7 дней от сегодня.

import { useState } from "react";
import { addDays, fmtDate, fmtDay, fmtNum, localISODate } from "../../shared/format";
import { useLive } from "../../shared/live";
import { navigate, setQuery, useRoute } from "../../shared/router";
import { TopBar } from "../../shared/shell";
import { Button, Empty, Field, Input, Loading, Modal, useAsync, useToast } from "../../shared/ui";
import { api, can, live, useCtx } from "../ctx";

export function WeekPage() {
  const r = useRoute();
  const start = r.query.get("start") || localISODate();
  const w = useAsync<any>(() => api.get("/scheduling/week", { start, days: 7 }), [start]);
  useLive(live, ["order.", "trip."], () => w.reload(), 800);
  return (
    <>
      <TopBar title="Неделя">
        <Button className="icon-btn" aria-label="Неделя назад" onClick={() => setQuery({ start: addDays(start, -7) })}>‹</Button>
        <Button size="small" variant="ghost" onClick={() => setQuery({ start: null })}>С сегодняшнего дня</Button>
        <Button className="icon-btn" aria-label="Неделя вперёд" onClick={() => setQuery({ start: addDays(start, 7) })}>›</Button>
      </TopBar>
      <div className="content">
        {!w.data ? <Loading error={w.error} onRetry={w.reload} /> : (
          <div className="panel table-wrap">
            <table className="table">
              <thead>
                <tr><th>День</th>{w.data.plants.map((p: any) => <th key={p.id} className="num">{p.name}</th>)}<th className="num">Всего, м³</th></tr>
              </thead>
              <tbody>
                {w.data.days.map((d: any) => (
                  <tr key={d.date} className="click" onClick={() => navigate("/schedule", { date: d.date })}>
                    <td><b>{fmtDay(d.date)}</b>{d.date === localISODate() && <span className="tag plain" style={{ marginLeft: 8 }}>сегодня</span>}</td>
                    {d.cells.map((c: any) => (
                      <td key={c.plant_id} className="num">
                        <div><b>{fmtNum(c.volume_m3, 0)} м³</b> <span className="muted small">{fmtNum(c.load_pct, 0)}%</span></div>
                        <div className={`bar thin${c.load_pct > 85 ? " warn" : ""}`} style={{ marginTop: 4 }}><i style={{ width: `${Math.min(100, c.load_pct)}%` }} /></div>
                      </td>
                    ))}
                    <td className="num strong">{fmtNum(d.total_m3, 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="muted small">Процент — загрузка установок завода за сутки. Нажмите на день, чтобы открыть график.</p>
        <Recurring />
      </div>
    </>
  );
}

const WEEK = ["пн", "вт", "ср", "чт", "пт", "сб", "вс"];

/** ФТ-2.4. Регулярные заявки: шаблоны «по дням недели с … по …». Новый шаблон — в форме заявки
 * (галочка «Регулярная заявка»); здесь — список, правка (применяется ко всем будущим дням) и остановка. */
function Recurring() {
  const { me } = useCtx();
  const list = useAsync<any[]>(() => api.get("/recurring-orders"), []);
  const [edit, setEdit] = useState<any | null>(null);
  useLive(live, ["recurring.", "order."], () => list.reload(), 1500);
  const rows = (list.data || []).filter((r) => r.active || r.date_to >= localISODate());
  return (
    <div className="panel">
      <div className="ph"><h2>Регулярные заявки · {rows.length}</h2><span className="muted small">Новая — в форме заявки, галочка «Регулярная заявка»</span></div>
      {!list.data ? <div className="pb"><Loading error={list.error} /></div> : rows.length === 0 ? <div className="pb"><Empty>Регулярных заявок нет.</Empty></div> : (
        <div className="table-wrap">
          <table className="table">
            <thead><tr><th>Клиент и объект</th><th>Марка</th><th className="num">м³</th><th>Время</th><th>Дни</th><th>Период</th><th>Состояние</th><th /></tr></thead>
            <tbody>{rows.map((r) => (
              <tr key={r.id}>
                <td><b>{r.customer_name}</b><div className="small muted">{r.site_name}</div></td>
                <td>{r.grade}</td><td className="num">{fmtNum(r.volume_m3)}</td><td className="num">{r.start_time}</td>
                <td className="small">{r.weekdays.length === 7 ? "каждый день" : r.weekdays.map((d: number) => WEEK[d]).join(", ")}</td>
                <td className="small">{fmtDate(r.date_from)} – {fmtDate(r.date_to)}</td>
                <td>{r.active ? <span className="tag s-done">действует</span> : <span className="tag plain">остановлена</span>}</td>
                <td>{can.createOrder(me.role) && <Button size="small" onClick={() => setEdit(r)}>Изменить</Button>}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
      {edit && <EditRecurring r={edit} onClose={() => setEdit(null)} onDone={() => { setEdit(null); list.reload(); }} />}
    </div>
  );
}

function EditRecurring({ r, onClose, onDone }: { r: any; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [v, setV] = useState({ volume_m3: String(r.volume_m3), start_time: r.start_time, weekdays: [...r.weekdays] as number[], date_to: r.date_to, active: r.active });
  const [busy, setBusy] = useState(false);
  const save = async (patch?: Partial<typeof v>) => {
    const x = { ...v, ...patch };
    setBusy(true);
    try {
      const res = await api.patch(`/recurring-orders/${r.id}`, {
        version: r.version, volume_m3: Number(String(x.volume_m3).replace(",", ".")), start_time: x.start_time,
        weekdays: x.weekdays, date_to: x.date_to, active: x.active,
      });
      const g = res.generated || {};
      toast.ok(x.active ? `Шаблон изменён: будущие заявки пересозданы (${g.created ?? 0})` : "Регулярная заявка остановлена: будущие заявки сняты");
      onDone();
    } catch (e) { toast.error(e); } finally { setBusy(false); }
  };
  return (
    <Modal title={`Регулярная заявка: ${r.customer_name}`} onClose={onClose} actions={<>
      {r.active && <Button variant="danger" disabled={busy} onClick={() => save({ active: false })}>Остановить</Button>}
      <Button onClick={onClose}>Отмена</Button>
      <Button variant="primary" disabled={busy || !v.weekdays.length} onClick={() => save()}>Сохранить</Button>
    </>}>
      <p className="small muted">{r.site_name}, {r.grade}. Изменения применяются ко всем будущим дням (кроме сегодня и заявок, изменённых отдельно).</p>
      <div className="form-grid">
        <Field label="Объём, м³"><Input inputMode="decimal" value={v.volume_m3} onChange={(e) => setV({ ...v, volume_m3: e.target.value })} /></Field>
        <Field label="Время первой машины"><Input type="time" step={300} value={v.start_time} onChange={(e) => setV({ ...v, start_time: e.target.value })} /></Field>
        <Field label="Дни недели" wide>
          <div className="row tight">{WEEK.map((w, i) => (
            <label key={w} className="check"><input type="checkbox" checked={v.weekdays.includes(i)}
              onChange={(e) => setV({ ...v, weekdays: e.target.checked ? [...v.weekdays, i].sort() : v.weekdays.filter((d) => d !== i) })} />{w}</label>
          ))}</div>
        </Field>
        <Field label="Повторять по"><Input type="date" value={v.date_to} onChange={(e) => setV({ ...v, date_to: e.target.value })} /></Field>
      </div>
    </Modal>
  );
}
